import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Pollutant } from "@/types/pollutant/pollutant.types";
import { InformationSource } from "@/types/information-source/information-source.types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { pollutantService } from "@/services/pollutant/pollutantService";
import { useData } from "@/hooks/useData";

interface PollutantFormProps {
  isOpen: boolean;
  onClose: () => void;
  pollutantToEdit?: Pollutant | null;
  onSuccess: () => void;
}

export const PollutantForm = ({
  isOpen,
  onClose,
  pollutantToEdit,
  onSuccess,
}: PollutantFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [poluVma, setPoluVma] = useState<number | undefined>(undefined);
  const [sourceId, setSourceId] = useState<string>("");

  // Validation state
  const [nameError, setNameError] = useState("");
  const [poluVmaError, setPoluVmaError] = useState("");
  const [sourceError, setSourceError] = useState("");

  // Fetch information sources for the dropdown
  const { data: infoSources, loading: loadingInfoSources } =
    useData<InformationSource>({
      collectionName: "setup_infoSources",
    });

  // Initialize form when editing
  useEffect(() => {
    if (pollutantToEdit) {
      setName(pollutantToEdit.name || "");
      setPoluVma(pollutantToEdit.polu_vma);
      setSourceId(pollutantToEdit.sourceId || "");
    } else {
      // Reset form for new creation
      setName("");
      setPoluVma(undefined);
      setSourceId("");
    }
    // Reset validation errors when opening/closing the form
    setNameError("");
    setPoluVmaError("");
    setSourceError("");
  }, [pollutantToEdit, isOpen]);

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("validation.required"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate VMA if provided
    if (poluVma !== undefined) {
      if (isNaN(poluVma) || poluVma < 0) {
        setPoluVmaError(t("pollutants.validation.invalidValue"));
        isValid = false;
      } else {
        setPoluVmaError("");
      }
    }

    // Validate source if VMA is provided
    if (poluVma !== undefined && !sourceId) {
      setSourceError(t("pollutants.validation.sourceRequired"));
      isValid = false;
    } else {
      setSourceError("");
    }

    return isValid;
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setPoluVma(undefined);
      return;
    }

    if (value === "." || value === "-" || value === "-.") {
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setPoluVma(numValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const pollutantData: Partial<Pollutant> = {
        name: name.trim(),
      };

      // Add VMA and source if VMA is provided
      if (poluVma !== undefined) {
        pollutantData.polu_vma = poluVma;
        if (sourceId) {
          pollutantData.sourceId = sourceId;
        }
      }

      if (pollutantToEdit) {
        // Update existing pollutant
        await pollutantService.edit(
          pollutantToEdit.id || "",
          pollutantData,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("pollutants.updated"),
        });
      } else {
        // Create new pollutant
        await pollutantService.create(
          pollutantData as Omit<Pollutant, "id" | "createdAt" | "updatedAt">,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("pollutants.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving pollutant:", error);
      toast({
        title: t("error"),
        description: pollutantToEdit
          ? t("pollutants.updateError")
          : t("pollutants.createError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out archived info sources
  const activeInfoSources =
    infoSources?.filter((source) => !source.isArchived) || [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {pollutantToEdit
              ? t("pollutants.editTitle")
              : t("pollutants.addTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name" className="flex items-center gap-1">
                {t("pollutants.name")}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={nameError ? "border-red-500" : ""}
                disabled={isLoading}
              />
              {nameError && <p className="text-red-500 text-sm">{nameError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="poluVma">{t("pollutants.polu_vma")}</Label>
              <Input
                id="poluVma"
                type="number"
                step="any"
                min="0"
                placeholder={t("pollutants.polu_vmaPlaceholder")}
                value={poluVma === undefined ? "" : poluVma}
                onChange={handleNumberInputChange}
                className={poluVmaError ? "border-red-500" : ""}
                disabled={isLoading}
              />
              {poluVmaError && (
                <p className="text-red-500 text-sm">{poluVmaError}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="sourceId">{t("pollutants.source")}</Label>
              <Select
                value={sourceId}
                onValueChange={(value) => {
                  setSourceId(value);
                  setSourceError("");
                }}
                disabled={
                  isLoading || loadingInfoSources || poluVma === undefined
                }
              >
                <SelectTrigger id="sourceId" className={`w-full ${sourceError ? "border-red-500" : ""}`}>
                  <SelectValue
                    placeholder={t("pollutants.sourcePlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {activeInfoSources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sourceError && (
                <p className="text-red-500 text-sm">{sourceError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 hover:bg-gray-100"
              disabled={isLoading}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isLoading ? t("saving") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
